<template>
  <div class="mt-3">
    <label for="fullAddress" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('address.fullAddressAutocomplete') }}
    </label>
    <div class="relative">
      <input
        id="fullAddress"
        v-model.trim="fullAddressQuery"
        type="text"
        name="fullAddress"
        class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
        :placeholder="$t('address.fullAddressPlaceholder')"
        @input="onFullAddressInput"
        @focus="showFullAddressSuggestions = true"
        @blur="hideFullAddressSuggestions"
      />
      <div
        v-if="showFullAddressSuggestions"
        class="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto"
      >
        <!-- Display suggestions -->
        <div
          v-for="suggestion in fullAddressSuggestions"
          :key="suggestion.place_id"
          class="px-4 py-2 cursor-pointer hover:bg-gray-100 text-left"
          @mousedown.prevent="selectFullAddressSuggestion(suggestion)"
        >
          <!-- Display formatted suggestion here, passing the full suggestion object -->
          {{ formatAddressSuggestion(suggestion) }}
        </div>

        <!-- No results found message -->
        <div
          v-if="
            fullAddressSuggestions.length === 0 &&
            fullAddressQuery.length > 0 &&
            !loadingSuggestions &&
            loadingSuggestionsNum > 0
          "
          class="px-4 py-2 text-gray-500 text-left"
        >
          {{ $t('address.noResultsFound') }}
        </div>

        <!-- Loading indicator (optional but good practice) -->
        <div v-if="loadingSuggestions" class="px-4 py-2 text-gray-500 text-left">
          {{ $t('address.loadingSuggestions') }}
        </div>
      </div>
    </div>
  </div>

  <div class="mt-3">
    <div @click="openMap()" class="btn btn-primary mr-1 mb-2">
      {{ $t('address.getFromMap') }}
    </div>
  </div>

  <!-- Hidden/Disabled Fields remain as before -->
  <div class="mt-3 hidden">
    <label for="street" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('address.street') }}
    </label>
    <input
      id="street"
      v-model.trim="validate.street.$model"
      type="text"
      name="street"
      disabled="true"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      placeholder=""
    />
  </div>
  <div class="mt-3 hidden">
    <label for="streetNumber" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('address.streetNumber') }}
    </label>
    <input
      id="streetNumber"
      v-model.trim="validate.streetNumber.$model"
      type="text"
      name="streetNumber"
      disabled="true"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      placeholder=""
    />
  </div>
  <div class="mt-3 hidden">
    <label for="city" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('address.city') }}
    </label>
    <input
      id="city"
      v-model.trim="validate.city.$model"
      type="text"
      name="city"
      disabled="true"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      placeholder=""
    />
  </div>
  <div class="mt-3 hidden">
    <label for="zip" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('address.zip') }}
    </label>
    <input
      id="zip"
      v-model.trim="validate.zip.$model"
      type="text"
      name="zip"
      disabled="true"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      placeholder=""
    />
  </div>
  <div class="mt-3 hidden">
    <label for="state" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('address.country') }}
    </label>
    <TomSelect
      v-model="validate.state.$model"
      :options="{
        create: false,
        placeholder: $t('address.countrySelect')
      }"
      disabled="true"
      class="w-full"
    >
      <option value="SVK">Slovensko</option>
    </TomSelect>
  </div>
  <div class="mt-3 hidden">
    <label for="latitude" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('address.latitude') }}
    </label>
    <input
      id="latitude"
      v-model.trim="validate.latitude.$model"
      type="text"
      name="latitude"
      disabled="true"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      placeholder=""
    />
  </div>
  <div class="mt-3 hidden">
    <label for="longitude" class="form-label w-full flex flex-col sm:flex-row">
      {{ $t('address.longitude') }}
    </label>
    <input
      id="longitude"
      v-model.trim="validate.longitude.$model"
      type="text"
      name="longitude"
      disabled="true"
      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
      placeholder=""
    />
  </div>

  <div id="location-selector-modal" class="modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <a data-dismiss="modal" href="javascript:;" id="location-selector-modal-close">
          <XIcon class="w-8 h-8 text-gray-500" />
        </a>
        <div class="modal-body p-10 text-center">
          <div class="px-5 pb-8 text-center">
            <button
              type="button"
              @click="fetchAddressFromMap"
              class="btn btn-primary btn-block"
            >
              {{ $t('address.loadAddress') }}
            </button>
          </div>
          <LocationSelector
            v-model:selectedLocation="selectedLocation"
            ref="locationSelector"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, toRefs, reactive, computed, watch } from 'vue'
import { helpers, decimal } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import axios from 'axios'

import LocationSelector from '../../components/openstreetmap/LocationSelector.vue'

// Custom debounce function
const customDebounce = (func, delay) => {
  let timeout
  return function (...args) {
    const context = this
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(context, args), delay)
  }
}

export default defineComponent({
  components: {
    LocationSelector
  },

  props: {
    address: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      loadingSuggestions: false,
      loadingSuggestionsNum: 0
    }
  },

  methods: {
    openMap() {
      this.showMapModal()
      setTimeout(this.$refs.locationSelector.reloadMap, 500)
    },
    getAddressData() {
      return { ...this.formData }
    },
    async fetchAddressFromMap() {
      if (
        undefined === this.selectedLocation[1] ||
        undefined === this.selectedLocation[0]
      ) {
        return
      }
      const response = await this.reverseGeocode(
        this.selectedLocation[1],
        this.selectedLocation[0]
      )
      if (response) {
        this.updateFieldsFromSuggestion(response)
      }
      document.getElementById('location-selector-modal-close')?.click()
      this.$emit('update:address', this.getAddressData())
    },
    async reverseGeocode(lat, lon) {
      try {
        const response = await axios.get('https://nominatim.openstreetmap.org/reverse', {
          params: {
            format: 'jsonv2',
            lat: lat,
            lon: lon,
            addressdetails: 1
          }
        })
        return response.data
      } catch (error) {
        console.error('Error fetching address:', error)
        return null
      }
    },
    async searchNominatim(query) {
      if (!query.trim()) {
        this.fullAddressSuggestions = []
        return
      }

      this.loadingSuggestions = true
      this.loadingSuggestionsNum++

      try {
        const params = {
          format: 'json',
          q: query,
          addressdetails: 1, // Essential for getting address parts
          limit: 5,
          countrycodes: 'sk'
        }

        const response = await axios.get('https://nominatim.openstreetmap.org/search', {
          params
        })

        this.fullAddressSuggestions = response.data
      } catch (error) {
        console.error('Error searching Nominatim:', error)
        this.fullAddressSuggestions = []
      } finally {
        this.loadingSuggestions = false
      }
    },
    selectFullAddressSuggestion(suggestion) {
      this.updateFieldsFromSuggestion(suggestion)
      this.showFullAddressSuggestions = false
      this.$emit('update:address', this.getAddressData())
    },
    updateFieldsFromSuggestion(suggestion) {
      // Set the main autocomplete input field value using the formatter
      this.fullAddressQuery = this.formatAddressParts(suggestion)

      // Populate hidden form fields
      const address = suggestion.address || {}
      this.validate.street.$model = address.road ?? ''
      this.validate.streetNumber.$model = address.house_number ?? ''
      this.validate.city.$model = address.city ?? address.town ?? address.village ?? ''
      this.validate.zip.$model = address.postcode ?? ''
      this.validate.state.$model = 'SVK'
      this.validate.latitude.$model = suggestion.lat
      this.validate.longitude.$model = suggestion.lon
      this.selectedLocation = [parseFloat(suggestion.lon), parseFloat(suggestion.lat)]
    },
    hideFullAddressSuggestions() {
      setTimeout(() => {
        this.showFullAddressSuggestions = false
      }, 150)
    },
    setData(data) {
      this.formData.street = data.street ?? ''
      this.formData.streetNumber = data.streetNumber ?? ''
      this.formData.city = data.city ?? ''
      this.formData.zip = data.zip ?? ''
      this.formData.state = 'SVK'
      this.formData.latitude = data.latitude ?? ''
      this.formData.longitude = data.longitude ?? ''
      if (data.longitude && data.latitude) {
        this.selectedLocation = [parseFloat(data.longitude), parseFloat(data.latitude)]
        this.reverseGeocode(data.latitude, data.longitude).then((response) => {
          if (response) {
            this.fullAddressQuery = this.formatAddressParts(response)
          } else {
            this.fullAddressQuery = ''
          }
        })
      } else {
        this.selectedLocation = ['', '']
        this.fullAddressQuery = ''
      }
    },
    // Modified to accept the full suggestion object
    formatAddressParts(suggestion) {
      const parts = []
      const address = suggestion.address || {} // Ensure address exists

      // Prioritize common name/description from the main suggestion object
      // if it's not a generic street name

      parts.push(suggestion.name)

      if (address.house_number) parts.push(address.house_number)
      if (address.road) parts.push(address.road)

      let city = address.city || address.town || address.village
      if (city) parts.push(city)

      if (address.postcode) parts.push(address.postcode)

      return parts.filter(Boolean).join(', ') // Filter out any empty strings
    },
    // This method is called in the template to format each suggestion in the list
    formatAddressSuggestion(suggestion) {
      return this.formatAddressParts(suggestion)
    }
  },

  emits: ['update:address'],

  setup(props, { emit }) {
    const selectedLocation = ref(['', ''])
    const fullAddressQuery = ref('')
    const fullAddressSuggestions = ref([])
    const showFullAddressSuggestions = ref(false)

    const zipValidator = helpers.regex(/^([0-9 ]{5,})?$/)
    const formData = reactive({
      street: '',
      streetNumber: '',
      city: '',
      zip: '',
      state: 'SVK',
      latitude: '',
      longitude: ''
    })

    const debouncedSearchNominatim = customDebounce(function (query) {
      this.searchNominatim(query)
    }, 1000)

    const onFullAddressInput = function () {
      debouncedSearchNominatim.call(this, this.fullAddressQuery)
    }

    watch(
      () => props.address,
      (newAddress) => {
        if (newAddress) {
          formData.street = newAddress.street ?? ''
          formData.streetNumber = newAddress.streetNumber ?? ''
          formData.city = newAddress.city ?? ''
          formData.zip = newAddress.zip ?? ''
          formData.state = 'SVK'
          formData.latitude = newAddress.latitude ?? ''
          formData.longitude = newAddress.longitude ?? ''

          if (newAddress.longitude && newAddress.latitude) {
            selectedLocation.value = [
              parseFloat(newAddress.longitude),
              parseFloat(newAddress.latitude)
            ]
            // A note here: calling formatAddressParts from `watch` in setup directly to set fullAddressQuery
            // is still complex due to `this` context for methods.
            // The `setData` method should be used by the parent to update the component
            // with address data, and `setData` now handles setting fullAddressQuery.
          } else {
            selectedLocation.value = ['', '']
            fullAddressQuery.value = ''
          }
        }
      },
      { immediate: true, deep: true }
    )

    if (props.address) {
      formData.street = props.address.street ?? ''
      formData.streetNumber = props.address.streetNumber ?? ''
      formData.city = props.address.city ?? ''
      formData.zip = props.address.zip ?? ''
      formData.state = 'SVK'
      formData.latitude = props.address.latitude ?? ''
      formData.longitude = props.address.longitude ?? ''
      if (props.address.longitude && props.address.latitude) {
        selectedLocation.value = [
          parseFloat(props.address.longitude),
          parseFloat(props.address.latitude)
        ]
      }
    }

    const rules = {
      street: {},
      streetNumber: {},
      city: {},
      zip: {
        zipValidator: helpers.withMessage('form.constraint.zipFormat', zipValidator)
      },
      state: {},
      latitude: {
        decimal: helpers.withMessage('form.constraint.invalidNumber', decimal)
      },
      longitude: {
        decimal: helpers.withMessage('form.constraint.invalidNumber', decimal)
      }
    }
    const validate = useVuelidate(rules, toRefs(formData))

    const showMapModal = () => {
      cash('#location-selector-modal').modal('show')
    }
    const hideMapModal = () => {
      cash('#location-selector-modal').modal('hide')
    }

    return {
      validate,
      formData,
      selectedLocation,
      fullAddressQuery,
      fullAddressSuggestions,
      showFullAddressSuggestions,
      showMapModal,
      hideMapModal,
      onFullAddressInput
    }
  }
})
</script>

<style scoped>
/* Keep existing styles */
.relative {
  position: relative;
}
.absolute {
  position: absolute;
}
.z-10 {
  z-index: 10;
}
.w-full {
  width: 100%;
}
.bg-white {
  background-color: white;
}
.border {
  border-width: 1px;
}
.border-gray-300 {
  border-color: #d1d5db;
}
.rounded-md {
  border-radius: 0.375rem;
}
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
.mt-1 {
  margin-top: 0.25rem;
}
.max-h-60 {
  max-height: 15rem; /* 60 * 0.25rem */
}
.overflow-y-auto {
  overflow-y: auto;
}
.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}
.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.cursor-pointer {
  cursor: pointer;
}
.hover\:bg-gray-100:hover {
  background-color: #f3f4f6;
}
.text-left {
  text-align: left;
}
</style>
